import { ADMIN_PICKER_REASON_MAX } from '@carcheck/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import type { PickerAccessAction } from '../../../api/admin-pickers';

/**
 * Disable / enable reason dialog (AUT-400), after `DeactivateInspectorDialog`
 * and the mockup's confirm modal. BOTH actions need a reason (the server
 * requires one for enable too — it goes to `admin_logs.reason`), trimmed,
 * 1..`ADMIN_PICKER_REASON_MAX` (shared with the server's schema).
 *
 * Disable warns that the links go dark for good; enable warns that revoked
 * links do not come back.
 */
const schema = z.object({
  reason: z
    .string()
    .trim()
    .min(1, 'pickers.reasonDialog.reasonRequired')
    .max(ADMIN_PICKER_REASON_MAX, 'pickers.reasonDialog.reasonMax'),
});

type ReasonForm = z.infer<typeof schema>;

export interface PickerReasonDialogProps {
  open: boolean;
  action: PickerAccessAction;
  /** Shown under the title — what the server sent (maybe redacted). */
  subject: string;
  activeLinkCount: number;
  loading?: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export const PickerReasonDialog = ({
  open,
  action,
  subject,
  activeLinkCount,
  loading = false,
  onConfirm,
  onCancel,
}: PickerReasonDialogProps): ReactElement => {
  const { t } = useTranslation();
  const ns = `pickers.${action}Dialog`;
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<ReasonForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { reason: '' },
  });

  useEffect(() => {
    if (open) reset({ reason: '' });
  }, [open, reset]);

  const submit = ({ reason }: ReasonForm): void => {
    if (!loading) onConfirm(reason.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(`${ns}.title`)}
        <Typography variant="body2" color="text.secondary">
          {subject}
        </Typography>
      </DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Alert severity={action === 'disable' ? 'error' : 'warning'}>
            {t(`${ns}.warning`, { count: activeLinkCount })}
          </Alert>
          <TextField
            {...register('reason')}
            fullWidth
            multiline
            minRows={2}
            size="small"
            disabled={loading}
            label={t('pickers.reasonDialog.reasonLabel')}
            slotProps={{ htmlInput: { 'data-testid': 'picker-reason-input' } }}
            error={Boolean(errors.reason)}
            helperText={
              errors.reason
                ? t(errors.reason.message ?? '', { max: ADMIN_PICKER_REASON_MAX })
                : t('pickers.reasonDialog.reasonHint')
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color={action === 'disable' ? 'error' : 'primary'}
            disabled={loading || !isValid}
            loading={loading}
            data-testid="picker-reason-confirm"
          >
            {t(`${ns}.confirm`, { count: activeLinkCount })}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
